import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

interface EventQRProps {
  url: string;
  title: string;
  /** Edge length of the rendered QR in pixels. Defaults to 256. */
  size?: number;
}

/**
 * SVG QR code with Copy Link / Download PNG / Print controls. The SVG is
 * rendered inline so printing and screen sizing stay crisp; the PNG
 * download is generated on demand from a hidden canvas.
 */
export function EventQR({ url, title, size = 256 }: EventQRProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toString(url, { type: "svg", margin: 1, width: size })
      .then((out) => {
        if (!cancelled) setSvg(out);
      })
      .catch(() => {
        if (!cancelled) setSvg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url, size]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const downloadPng = async () => {
    const canvas = canvasRef.current ?? document.createElement("canvas");
    canvasRef.current = canvas;
    await QRCode.toCanvas(canvas, url, { margin: 2, width: 1024 });
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${slugifyTitle(title)}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printQr = () => {
    if (!svg) return;
    const win = window.open("", "_blank", "width=800,height=900");
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>${escapeHtml(title)} — Finally QR</title>
    <style>
      body { font-family: system-ui, sans-serif; display: flex; flex-direction: column; align-items: center; padding: 40px; color: #1a1a1a; }
      h1 { font-size: 22px; margin: 0 0 4px; }
      p { color: #6c757d; font-size: 14px; margin: 0 0 32px; }
      .qr { width: 480px; max-width: 80vw; }
      .url { margin-top: 24px; font-family: ui-monospace, SFMono-Regular, monospace; font-size: 14px; color: #2563EB; word-break: break-all; }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(title)}</h1>
    <p>Scan to join the event on Finally</p>
    <div class="qr">${svg}</div>
    <div class="url">${escapeHtml(url)}</div>
    <script>window.onload = () => window.print();</script>
  </body>
</html>`);
    win.document.close();
  };

  return (
    <div className="rounded-[20px] border border-[#e2e8f0] bg-white p-6 text-center">
      <div className="mx-auto inline-block rounded-[12px] bg-white p-3">
        {svg ? (
          <div
            className="inline-block"
            style={{ width: size, height: size }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <div
            className="grid place-items-center rounded-md bg-[#f8fafc] text-xs text-[#6c757d]"
            style={{ width: size, height: size }}
          >
            Generating...
          </div>
        )}
      </div>
      <p className="mt-3 text-[13px] text-[#6c757d]">Print and place at the entrance or on tables</p>

      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={copyLink}
          className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-2 text-sm font-semibold text-[#1a1a1a] hover:bg-[#f8fafc]"
        >
          {copied ? "✓ Copied" : "Copy Link"}
        </button>
        <button
          type="button"
          onClick={downloadPng}
          className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-2 text-sm font-semibold text-[#1a1a1a] hover:bg-[#f8fafc]"
        >
          Download PNG
        </button>
        <button
          type="button"
          onClick={printQr}
          disabled={!svg}
          className="rounded-xl bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-60"
        >
          Print
        </button>
      </div>
    </div>
  );
}

function slugifyTitle(title: string): string {
  return title.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "") || "event";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
